import { type MutableRefObject, useEffect, useRef } from 'react'

import { useAuth } from 'src/auth'
import { subscribeToLogout } from 'src/lib/crossTabLogout'

/**
 * When another tab logs out, re-checks this tab's session so it flips to logged
 * out too and private routes send it to the login page. It reauthenticates
 * rather than logging out, so receiving a logout never re-broadcasts it, and
 * can't clear a session the user started again in the meantime.
 */
export const useRecheckSessionOnLogout = () => {
  const { isAuthenticated, reauthenticate } = useAuth()
  const isReauthenticating = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) return undefined

    return subscribeToLogout(() =>
      reauthenticateUnlessInFlight(isReauthenticating, reauthenticate)
    )
  }, [isAuthenticated, reauthenticate])
}

const reauthenticateUnlessInFlight = (
  isReauthenticating: MutableRefObject<boolean>,
  reauthenticate: () => Promise<void>
) => {
  if (isReauthenticating.current) return
  isReauthenticating.current = true
  void reauthenticate().finally(() => {
    isReauthenticating.current = false
  })
}
